import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton, List, ListItemButton, ListItemText, 
  ListItemAvatar, Avatar, Badge, Divider, Stack, Chip, CircularProgress,
  InputAdornment, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, DialogContentText, Alert
} from '@mui/material';
import {
  Send, AttachFile, Person, Phone, Done, DoneAll, Schedule, Error as ErrorIcon,
  Search, WhatsApp, Settings, PersonAdd, LinkOff, Download, Image, AudioFile,
  VideoFile, InsertDriveFile, Close, VisibilityOff, Psychology as AiIcon, LinkRounded,
  OpenInNew, ArrowBack, Wifi, WifiOff, Sync
} from '@mui/icons-material';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { whatsappApi, customersApi, aiWhatsappApi } from '../../services/api';
import useWhatsApp from '../../hooks/useWhatsApp';
import AiWhatsappPanel from '../../components/ai/AiWhatsappPanel';
import WhatsappSetup from './WhatsappSetup';
import { useMobile } from '../../hooks/useMobile';
import { getMediaBaseUrl } from '../../utils/runtimeUrls';

const MEDIA_BASE_URL = getMediaBaseUrl();
const ALL_LINES_VALUE = 'all';

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const now = new Date();
  const isToday = d.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = d.toDateString() === yesterday.toDateString();
  
  if (isToday) return d.toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });
  if (isYesterday) return 'Ieri';
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' });
}

function StatusIcon({ status }) {
  switch (status) {
    case 'queued':
    case 'sending':
      return <Schedule sx={{ fontSize: 14, color: 'text.disabled' }} />;
    case 'sent':
      return <Done sx={{ fontSize: 14, color: 'text.disabled' }} />;
    case 'delivered':
      return <DoneAll sx={{ fontSize: 14, color: 'text.disabled' }} />;
    case 'read':
      return <DoneAll sx={{ fontSize: 14, color: '#53bdeb' }} />;
    case 'held':
      return <Tooltip title="Invio sospeso"><Schedule sx={{ fontSize: 14, color: 'warning.main' }} /></Tooltip>;
    case 'delivery_unknown':
      return <Tooltip title="Esito da verificare: il messaggio non viene reinviato automaticamente"><ErrorIcon sx={{ fontSize: 14, color: 'warning.main' }} /></Tooltip>;
    case 'failed':
      return <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />;
    default:
      return null;
  }
}

// Componente per rendering media nei messaggi
function MediaContent({ msg, onDownload }) {
  const [downloading, setDownloading] = useState(false);
  const [lightbox, setLightbox] = useState(false);

  const mediaUrl = msg.media_url ? `${MEDIA_BASE_URL}/${msg.media_url}` : null;
  const mimeType = msg.media_mime_type || '';

  // Se il media non è ancora scaricato, mostra pulsante download
  if (!msg.media_url && msg.message_type !== 'text') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
        {msg.message_type === 'image' && <Image sx={{ color: 'text.secondary' }} />}
        {msg.message_type === 'audio' && <AudioFile sx={{ color: 'text.secondary' }} />}
        {msg.message_type === 'video' && <VideoFile sx={{ color: 'text.secondary' }} />}
        {msg.message_type === 'document' && <InsertDriveFile sx={{ color: 'text.secondary' }} />}
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          {msg.message_type === 'image' ? 'Immagine' : msg.message_type === 'audio' ? 'Audio' : msg.message_type === 'video' ? 'Video' : 'Documento'}
        </Typography>
        <IconButton
          size="small"
          disabled={downloading}
          onClick={async () => {
            setDownloading(true);
            try { await onDownload(msg.wa_message_id); } catch (e) { /* ignore */ }
            setDownloading(false);
          }}
        >
          {downloading ? <CircularProgress size={16} /> : <Download fontSize="small" />}
        </IconButton>
      </Box>
    );
  }

  if (!mediaUrl) return null;

  // Immagine
  if (mimeType.startsWith('image/')) {
    return (
      <>
        <Box
          component="img"
          src={mediaUrl}
          alt={msg.media_filename || 'Immagine'}
          sx={{ maxWidth: '100%', maxHeight: 300, borderRadius: 1, mb: 0.5, cursor: 'pointer', display: 'block' }}
          onClick={() => setLightbox(true)}
        />
        {lightbox && (
          <Dialog open onClose={() => setLightbox(false)} maxWidth="lg">
            <DialogContent sx={{ p: 0 }}>
              <Box component="img" src={mediaUrl} alt="" sx={{ maxWidth: '90vw', maxHeight: '90vh' }} />
            </DialogContent>
          </Dialog>
        )}
      </>
    );
  }

  // Audio
  if (mimeType.startsWith('audio/')) {
    return (
      <Box sx={{ mb: 0.5 }}>
        <audio controls style={{ maxWidth: '100%', height: 36 }} src={mediaUrl} />
      </Box>
    );
  }

  // Video
  if (mimeType.startsWith('video/')) {
    return (
      <Box sx={{ mb: 0.5 }}>
        <video controls style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 4 }} src={mediaUrl} />
      </Box>
    );
  }

  // Documento / altro
  return (
    <Box
      component="a"
      href={mediaUrl}
      target="_blank"
      rel="noopener noreferrer"
      download={msg.media_filename}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, p: 1,
        bgcolor: 'action.hover', borderRadius: 1, textDecoration: 'none', color: 'inherit',
        '&:hover': { bgcolor: 'action.selected' }
      }}
    >
      <InsertDriveFile sx={{ color: 'primary.main' }} />
      <Typography variant="caption" sx={{ flex: 1 }} noWrap>
        {msg.media_filename || 'Documento'}
      </Typography>
      <Download fontSize="small" sx={{ color: 'text.secondary' }} />
    </Box>
  );
}

function MessageBubble({ msg, onDownloadMedia }) {
  const isOutbound = msg.direction === 'outbound';
  return (
    <Box sx={{ display: 'flex', justifyContent: isOutbound ? 'flex-end' : 'flex-start', mb: 1 }}>
      <Paper
        elevation={1}
        sx={{
          px: 1.5, py: 1, maxWidth: { xs: '85%', sm: '70%' }, borderRadius: 2,
          bgcolor: isOutbound ? '#dcf8c6' : '#fff',
          borderTopRightRadius: isOutbound ? 0 : 2,
          borderTopLeftRadius: isOutbound ? 2 : 0,
        }}
      >
        <MediaContent msg={msg} onDownload={onDownloadMedia} />
        {msg.body && (
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {msg.body}
          </Typography>
        )}
        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center" sx={{ mt: 0.5 }}>
          <Typography variant="caption" color="text.disabled">
            {formatTime(msg.created_at)}
          </Typography>
          {isOutbound && <StatusIcon status={msg.status} />}
        </Stack>
      </Paper>
    </Box>
  );
}

function buildPendingConversation(phoneNumber, state = {}) {
  if (!phoneNumber) return null;

  const customer = state?.customer || null;
  const contactName = state?.contactName || customer?.nome || String(phoneNumber);

  return {
    phone_number: String(phoneNumber),
    contact_name: contactName,
    customer,
    customer_id: customer?.id || null,
    unread_count: 0,
    last_message: null,
    last_message_at: null,
    suggested_customer: null,
    whatsapp_line_id: state?.whatsappLineId || state?.line_id || null,
    line_id: state?.whatsappLineId || state?.line_id || null,
  };
}

// Dialog per associare un cliente a una conversazione WhatsApp
function AssignCustomerDialog({ open, onClose, phone, lineId, onAssigned, fullScreen = false }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  // Carica suggerimenti al mount
  useEffect(() => {
    if (!open || !phone) return;
    setLoadingSuggestions(true);
    whatsappApi.suggestCustomers(phone, lineId)
      .then(r => setSuggestions(r.data?.suggestions || []))
      .catch(() => {})
      .finally(() => setLoadingSuggestions(false));
  }, [open, phone, lineId]);

  // Ricerca manuale
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await customersApi.getAll({ search: searchQuery, limit: 10 });
        setSearchResults(res.data?.data || res.data || []);
      } catch (e) {
        setSearchResults([]);
      }
      setSearching(false);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  const handleSelect = async (customerId) => {
    try {
      await whatsappApi.assignCustomer(phone, customerId, lineId);
      onAssigned();
      onClose();
    } catch (e) {
      console.error('Errore associazione:', e);
    }
  };

  const customerLabel = (c) => {
    const name = c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim();
    return c.cod_cli ? `[${c.cod_cli}] ${name}` : name;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle>Associa Cliente</DialogTitle>
      <DialogContent>
        {/* Suggerimenti automatici */}
        {loadingSuggestions ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={24} /></Box>
        ) : suggestions.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Suggerimenti automatici</Typography>
            {suggestions.map((s) => (
              <Paper
                key={s.customer.id}
                variant="outlined"
                sx={{ p: 1.5, mb: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                onClick={() => handleSelect(s.customer.id)}
              >
                <Typography variant="subtitle2">{customerLabel(s.customer)}</Typography>
                <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {s.reasons.map((r, i) => (
                    <Chip key={i} label={r} size="small" variant="outlined" color="primary" />
                  ))}
                </Stack>
              </Paper>
            ))}
            <Divider sx={{ my: 2 }} />
          </Box>
        )}

        {/* Ricerca manuale */}
        <Typography variant="subtitle2" gutterBottom>Ricerca manuale</Typography>
        <TextField
          fullWidth
          size="small"
          placeholder="Cerca per nome, codice cliente, ragione sociale..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
            endAdornment: searching ? <CircularProgress size={18} /> : null
          }}
          sx={{ mb: 1 }}
        />
        {searchResults.length > 0 && (
          <List dense sx={{ maxHeight: 250, overflow: 'auto' }}>
            {searchResults.map((c) => (
              <ListItemButton key={c.id} onClick={() => handleSelect(c.id)}>
                <ListItemText
                  primary={customerLabel(c)}
                  secondary={[c.email, c.cellulare, c.telefono].filter(Boolean).join(' · ')}
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annulla</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function WhatsappInbox() {
  const { isMobile } = useMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { connectionStatus, onMessage, lines = [] } = useWhatsApp();
  const [selectedConv, setSelectedConv] = useState(null);
  const [selectedLineId, setSelectedLineId] = useState(ALL_LINES_VALUE);
  const [messageText, setMessageText] = useState('');
  const [sending, setSending] = useState(false);
  const [syncingChats, setSyncingChats] = useState(false);
  const [syncNotice, setSyncNotice] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [fetchingHistory, setFetchingHistory] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [attachFile, setAttachFile] = useState(null);
  const [profilePicErrors, setProfilePicErrors] = useState({});
  // Hide contacts
  const [hideDialogPhone, setHideDialogPhone] = useState(null);
  const [hideDialogName, setHideDialogName] = useState('');
  // AI Panel
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiActionId, setAiActionId] = useState(null);
  const [aiInvoicesData, setAiInvoicesData] = useState([]);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const messagesEndRef = useRef(null);
  const fetchedChatsRef = useRef(new Set());
  const fileInputRef = useRef(null);
  const [searchParams] = useSearchParams();

  const viewingAllLines = selectedLineId === ALL_LINES_VALUE;
  const selectedLine = viewingAllLines ? null : (lines.find(line => Number(line.id) === Number(selectedLineId)) || null);
  const activeLineId = viewingAllLines ? null : (selectedLine?.id || selectedLineId || null);
  const conversationLineKey = activeLineId || ALL_LINES_VALUE;
  const hasReadyLine = lines.length ? lines.some(line => line.status === 'ready') : connectionStatus === 'ready';
  const selectedLineReady = viewingAllLines ? hasReadyLine : (!activeLineId || selectedLine?.status === 'ready');
  const selectedConvLine = selectedConv?.whatsapp_line_id ? lines.find(line => Number(line.id) === Number(selectedConv.whatsapp_line_id)) : null;
  const selectedConversationReady = selectedConv?.whatsapp_line_id
    ? (selectedConvLine ? selectedConvLine.status === 'ready' : connectionStatus === 'ready')
    : selectedLineReady;

  useEffect(() => {
    if (selectedLineId === ALL_LINES_VALUE || !selectedLineId || !lines.length) return;
    const exists = lines.some(line => Number(line.id) === Number(selectedLineId));
    if (!exists) {
      setSelectedLineId(ALL_LINES_VALUE);
      setSelectedConv(null);
    }
  }, [lines, selectedLineId]);

  // Fetch conversazioni
  const { data: convData, isLoading: loadingConv } = useQuery({
    queryKey: ['whatsapp-conversations', conversationLineKey],
    queryFn: () => whatsappApi.getConversations(activeLineId ? { line_id: activeLineId } : {}).then(r => r.data?.data || []),
    refetchInterval: 10000,
    enabled: connectionStatus === 'ready' && selectedLineReady
  });

  // Sincronizza selectedConv con i dati aggiornati (customer, suggested_customer)
  useEffect(() => {
    if (!selectedConv || !convData) return;
    const updated = convData.find(c => c.phone_number === selectedConv.phone_number && Number(c.whatsapp_line_id || 0) === Number(selectedConv.whatsapp_line_id || 0));
    if (updated && (
      updated.customer_id !== selectedConv.customer_id ||
      updated.suggested_customer?.id !== selectedConv.suggested_customer?.id ||
      updated.whatsapp_line_id !== selectedConv.whatsapp_line_id
    )) {
      setSelectedConv(updated);
    }
  }, [convData, selectedConv]);

  useEffect(() => {
    const targetPhone = location.state?.phoneNumber || searchParams.get('phone');
    if (!targetPhone) return;

    const normalizedTarget = String(targetPhone);
    const targetLineId = location.state?.whatsappLineId || location.state?.line_id || activeLineId;
    const targetConversation = convData?.find((conversation) => conversation.phone_number === normalizedTarget && (!targetLineId || Number(conversation.whatsapp_line_id || 0) === Number(targetLineId || 0)))
      || buildPendingConversation(normalizedTarget, { ...location.state, whatsappLineId: targetLineId });
    if (!targetConversation) return;

    if (
      selectedConv?.phone_number !== normalizedTarget
      || Number(selectedConv?.whatsapp_line_id || 0) !== Number(targetConversation.whatsapp_line_id || 0)
      || (targetConversation.customer_id && selectedConv?.customer_id !== targetConversation.customer_id)
    ) {
      setSelectedConv(targetConversation);
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [activeLineId, convData, location.pathname, location.state, navigate, searchParams, selectedConv]);

  // Fetch thread messaggi
  const { data: threadData, isLoading: loadingThread } = useQuery({
    queryKey: ['whatsapp-thread', selectedConv?.phone_number, selectedConv?.whatsapp_line_id || activeLineId],
    queryFn: () => {
      if (!selectedConv) return [];
      const identifier = selectedConv.phone_number;
      return whatsappApi.getConversation(identifier, { limit: 100, ...(selectedConv.whatsapp_line_id ? { line_id: selectedConv.whatsapp_line_id } : {}) }).then(r => r.data?.data || []);
    },
    refetchInterval: 15000,
    enabled: !!selectedConv && connectionStatus === 'ready' && selectedLineReady
  });

  // Ascolta nuovi messaggi via Socket.IO
  useEffect(() => {
    const unsubscribe = onMessage((data) => {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-thread'] });
    });
    return unsubscribe;
  }, [activeLineId, onMessage, queryClient]);

  // Fetch on-demand: quando l'utente apre una chat, carica la cronologia da WhatsApp
  useEffect(() => {
    if (!selectedConv || fetchingHistory) return;
    const phone = selectedConv.phone_number;
    const lineId = selectedConv.whatsapp_line_id || activeLineId || null;
    const fetchKey = `${lineId || 'default'}:${phone}`;
    if (!phone || fetchedChatsRef.current.has(fetchKey)) return;
    
    fetchedChatsRef.current.add(fetchKey);
    setFetchingHistory(true);
    whatsappApi.fetchMessages(phone, 50, lineId)
      .then(res => {
        if (res.data?.data?.imported > 0) {
          queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', selectedConv?.phone_number, lineId] });
          queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
        }
      })
      .catch(err => console.warn('Fetch history error:', err.message))
      .finally(() => setFetchingHistory(false));
  }, [activeLineId, selectedConv, queryClient, fetchingHistory]);

  // Scroll to bottom quando arrivano nuovi messaggi
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [threadData]);

  const handleSend = async () => {
    if ((!messageText.trim() && !attachFile) || !selectedConv || !selectedConversationReady) return;
    setSending(true);
    try {
      const lineId = selectedConv.whatsapp_line_id || activeLineId || null;
      if (attachFile) {
        // Invio media
        const formData = new FormData();
        formData.append('file', attachFile);
        formData.append('to', selectedConv.phone_number);
        if (messageText.trim()) formData.append('caption', messageText.trim());
        if (selectedConv.customer_id) formData.append('customerId', selectedConv.customer_id);
        if (lineId) formData.append('line_id', lineId);
        await whatsappApi.sendMedia(formData);
        setAttachFile(null);
      } else {
        // Invio testo
        await whatsappApi.sendMessage({
          to: selectedConv.phone_number,
          body: messageText.trim(),
          customerId: selectedConv.customer_id || null,
          line_id: lineId
        });
      }
      setMessageText('');
      queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', selectedConv?.phone_number, lineId] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    } catch (err) {
      console.error('Errore invio:', err);
    } finally {
      setSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleDownloadMedia = async (waMessageId) => {
    const lineId = selectedConv?.whatsapp_line_id || activeLineId || null;
    const res = await whatsappApi.downloadMedia(waMessageId, lineId);
    if (res.data?.success) {
      queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', selectedConv?.phone_number, lineId] });
    }
  };

  const handleSyncNow = async () => {
    if (!selectedLineReady || syncingChats) return;
    setSyncingChats(true);
    setSyncNotice(null);
    try {
      await whatsappApi.syncChats({
        all_lines: viewingAllLines,
        ...(activeLineId ? { line_id: activeLineId } : {}),
        max_chats: viewingAllLines ? 500 : 300,
        messages_limit: 50
      });
      setSyncNotice({ severity: 'info', message: viewingAllLines ? 'Sincronizzazione avviata per tutte le linee' : 'Sincronizzazione linea avviata' });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    } catch (e) {
      setSyncNotice({ severity: 'error', message: e.response?.data?.error || 'Errore avvio sincronizzazione' });
    } finally {
      setSyncingChats(false);
    }
  };

  const handleRemoveCustomer = async () => {
    if (!selectedConv) return;
    try {
      const lineId = selectedConv.whatsapp_line_id || activeLineId || null;
      await whatsappApi.removeCustomer(selectedConv.phone_number, lineId);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', selectedConv?.phone_number, lineId] });
      setSelectedConv(prev => prev ? { ...prev, customer: null, customer_id: null } : null);
    } catch (e) {
      console.error('Errore rimozione:', e);
    }
  };

  const handleCustomerAssigned = () => {
    const lineId = selectedConv?.whatsapp_line_id || activeLineId || null;
    queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
    queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', selectedConv?.phone_number, lineId] });
    // Aggiorna selectedConv alla prossima refetch
  };

  // --- QUICK LINK (suggerimento match telefono) ---
  const handleQuickLink = async (e, conv) => {
    e.stopPropagation();
    if (!conv.suggested_customer?.id) return;
    try {
      const lineId = conv.whatsapp_line_id || activeLineId || null;
      await whatsappApi.assignCustomer(conv.phone_number, conv.suggested_customer.id, lineId);
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      if (selectedConv?.phone_number === conv.phone_number && Number(selectedConv?.whatsapp_line_id || 0) === Number(conv.whatsapp_line_id || 0)) {
        queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', conv.phone_number, lineId] });
      }
    } catch (err) {
      console.error('Errore collegamento rapido:', err);
    }
  };

  // --- HIDE CONTACT ---
  const handleHideContact = async (phone, contactName) => {
    try {
      await whatsappApi.hideContact(phone, { contact_name: contactName, reason: 'non_cliente' });
      queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
      if (selectedConv?.phone_number === phone) setSelectedConv(null);
      setHideDialogPhone(null);
    } catch (e) {
      console.error('Errore nascondimento:', e);
    }
  };

  // --- AI ASSISTANT ---
  const handleAiAnalyze = async () => {
    if (!selectedConv) return;
    setAiLoading(true);
    setAiError(null);
    setAiAnalysis(null);
    setAiPanelOpen(true);
    try {
      const res = await aiWhatsappApi.analyze(selectedConv.phone_number);
      const data = res.data;
      if (data.success) {
        setAiAnalysis({ ...data.analysis, model: data.model, provider: data.provider, analyzed_at: data.analyzed_at });
        setAiActionId(data.action_id);
        setAiInvoicesData(data.invoices_data || []);
      } else {
        setAiError(data.error || 'Errore analisi');
      }
    } catch (e) {
      setAiError(e.response?.data?.error || e.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiApprove = async (actionId, attachments) => {
    await aiWhatsappApi.approveAction(actionId, attachments);
    setAiPanelOpen(false);
    setAiAnalysis(null);
    setAiActionId(null);
    const lineId = selectedConv?.whatsapp_line_id || activeLineId || null;
    queryClient.invalidateQueries({ queryKey: ['whatsapp-thread', selectedConv?.phone_number, lineId] });
    queryClient.invalidateQueries({ queryKey: ['whatsapp-conversations'] });
  };

  const handleAiReject = async (actionId) => {
    await aiWhatsappApi.rejectAction(actionId);
    setAiPanelOpen(false);
    setAiAnalysis(null);
    setAiActionId(null);
  };

  const handleAiRegenerate = async (actionId, customPrompt, tone) => {
    setAiLoading(true);
    try {
      const res = await aiWhatsappApi.regenerateReply(actionId, customPrompt, tone);
      if (res.data?.success) {
        setAiAnalysis(prev => prev ? { ...prev, reply_draft: res.data.reply_draft, model: res.data.model, provider: res.data.provider } : prev);
      }
    } catch (e) {
      console.error('Errore rigenerazione:', e);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAiEditDraft = async (actionId, text) => {
    await aiWhatsappApi.editDraft(actionId, text);
  };

  const handleAiInsertDraft = (text) => {
    setMessageText(text);
    setAiPanelOpen(false);
  };

  // Reset AI state when conversation changes. Re-run analysis automatically
  // only if the assistant panel is already open for the operator.
  useEffect(() => {
    setAiAnalysis(null);
    setAiActionId(null);
    setAiInvoicesData([]);
    setAiError(null);

    const hasCustomer = selectedConv?.customer_id || selectedConv?.customer?.id;
    if (!hasCustomer || !selectedConv?.phone_number) {
      setAiLoading(false);
      setAiPanelOpen(false);
      return;
    }

    if (!aiPanelOpen) {
      setAiLoading(false);
      return;
    }

    setAiLoading(true);
    aiWhatsappApi.analyze(selectedConv.phone_number)
      .then(res => {
        const data = res.data;
        if (data.success) {
          setAiAnalysis({ ...data.analysis, model: data.model, provider: data.provider, analyzed_at: data.analyzed_at });
          setAiActionId(data.action_id);
          setAiInvoicesData(data.invoices_data || []);
        } else {
          setAiError(data.error || 'Errore analisi');
        }
      })
      .catch(e => setAiError(e.response?.data?.error || e.message))
      .finally(() => setAiLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiPanelOpen, selectedConv?.phone_number, selectedConv?.customer_id, selectedConv?.customer?.id]);

  const getLineById = (lineId) => lines.find(line => Number(line.id) === Number(lineId));
  const getLineLabel = (lineId, fallbackPhone = null) => {
    const line = getLineById(lineId);
    return line?.label || fallbackPhone || (lineId ? `Linea ${lineId}` : 'Linea');
  };

  const conversations = convData || [];
  const filteredConv = searchQuery
    ? conversations.filter(c => 
        (c.customer?.nome || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        (c.contact_name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.phone_number.includes(searchQuery)
      )
    : conversations;
  const messages = threadData || [];

  // Profile pic URL helper
  const getProfilePicSrc = (conversation) => {
    const phone = typeof conversation === 'string' ? conversation : conversation?.phone_number;
    if (profilePicErrors[phone]) return null;
    const avatarPath = typeof conversation === 'object' ? conversation?.profile_pic_url : null;
    if (!avatarPath) return null;
    if (/^https?:\/\//i.test(avatarPath)) return avatarPath;
    return `${MEDIA_BASE_URL}${avatarPath.startsWith('/') ? '' : '/'}${avatarPath}`;
  };

  const renderPageHeader = () => (
    <Paper
      elevation={0}
      sx={{
        px: { xs: 1.5, sm: 2 },
        py: 1,
        borderBottom: 1,
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexShrink: 0
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
        <WhatsApp color="success" />
        <Typography variant="subtitle1" fontWeight={700} noWrap>WhatsApp</Typography>
        {viewingAllLines ? (
          <Chip
            size="small"
            label="Tutte le linee"
            color={hasReadyLine ? 'success' : 'warning'}
            variant="outlined"
            sx={{ maxWidth: { xs: 150, sm: 220 } }}
          />
        ) : selectedLine && (
          <Chip
            size="small"
            label={selectedLine.label || `Linea ${selectedLine.id}`}
            color={selectedLine.status === 'ready' ? 'success' : 'warning'}
            variant="outlined"
            sx={{ maxWidth: { xs: 130, sm: 220 } }}
          />
        )}
      </Stack>
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <Tooltip title={viewingAllLines ? 'Sincronizza tutte le linee' : 'Sincronizza linea'}>
          <span>
            <IconButton color="primary" onClick={handleSyncNow} disabled={!selectedLineReady || syncingChats} aria-label="Sincronizza WhatsApp">
              {syncingChats ? <CircularProgress size={22} /> : <Sync />}
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Impostazioni WhatsApp">
          <IconButton color="primary" onClick={() => setSetupOpen(true)} aria-label="Impostazioni WhatsApp">
            <Settings />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );

  const renderSetupDialog = () => (
    <Dialog open={setupOpen} onClose={() => setSetupOpen(false)} fullScreen={isMobile} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pr: 1 }}>
        Impostazioni WhatsApp
        <IconButton aria-label="Chiudi impostazioni WhatsApp" onClick={() => setSetupOpen(false)}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <WhatsappSetup embedded />
      </DialogContent>
    </Dialog>
  );

  if (connectionStatus !== 'ready') {
    return (
      <Box sx={{ height: { xs: 'calc(100dvh - 88px)', sm: 'calc(100vh - 64px)' }, display: 'flex', flexDirection: 'column', bgcolor: 'background.paper' }}>
        {renderPageHeader()}
        <Box sx={{ flex: 1, p: 3, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Stack alignItems="center" spacing={2}>
            <WhatsApp sx={{ fontSize: 80, color: 'text.disabled' }} />
            <Typography variant="h5">WhatsApp non connesso</Typography>
            <Typography color="text.secondary">
              Connetti WhatsApp per iniziare a inviare e ricevere messaggi.
            </Typography>
            <Button variant="contained" startIcon={<Settings />} onClick={() => setSetupOpen(true)}>
              Apri impostazioni
            </Button>
          </Stack>
        </Box>
        {renderSetupDialog()}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: { xs: 'calc(100dvh - 88px)', sm: 'calc(100vh - 64px)' },
        minHeight: 0,
        overflow: 'hidden',
        borderRadius: { xs: 0, sm: 2 },
        bgcolor: 'background.paper'
      }}
    >
      {renderPageHeader()}
      <Box
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          bgcolor: 'background.paper'
        }}
      >
      {/* Sidebar: Lista Conversazioni */}
      {(!isMobile || !selectedConv) && (
      <Paper 
        elevation={0} 
        sx={{ 
          width: isMobile ? '100%' : 360,
          minWidth: isMobile ? 0 : 300,
          flex: isMobile ? 1 : 'none',
          minHeight: 0,
          borderRight: isMobile ? 0 : 1,
          borderColor: 'divider',
          display: 'flex', flexDirection: 'column'
        }}
      >
        {/* Header conversazioni */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <WhatsApp color="success" /> Chat
          </Typography>
          {lines.length > 0 && (
            <Stack direction="row" spacing={1} sx={{ mb: 1, overflowX: 'auto', pb: 0.5 }}>
              <Chip
                size="small"
                icon={<WhatsApp sx={{ fontSize: 14 }} />}
                label="Tutte"
                color={viewingAllLines ? 'primary' : 'default'}
                variant={viewingAllLines ? 'filled' : 'outlined'}
                onClick={() => {
                  setSelectedLineId(ALL_LINES_VALUE);
                  setSelectedConv(null);
                }}
              />
              {lines.map((line) => (
                <Chip
                  key={line.id}
                  size="small"
                  icon={line.status === 'ready' ? <Wifi sx={{ fontSize: 14 }} /> : <WifiOff sx={{ fontSize: 14 }} />}
                  label={line.label || `Linea ${line.id}`}
                  color={Number(activeLineId) === Number(line.id) ? 'primary' : 'default'}
                  variant={Number(activeLineId) === Number(line.id) ? 'filled' : 'outlined'}
                  onClick={() => {
                    setSelectedLineId(line.id);
                    setSelectedConv(null);
                  }}
                />
              ))}
            </Stack>
          )}
          {!selectedLineReady && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {viewingAllLines ? 'Nessuna linea WhatsApp connessa' : 'Linea WhatsApp non connessa'}
            </Alert>
          )}
          {syncNotice && (
            <Alert severity={syncNotice.severity} sx={{ mb: 1 }} onClose={() => setSyncNotice(null)}>
              {syncNotice.message}
            </Alert>
          )}
          <TextField
            size="small"
            fullWidth
            placeholder="Cerca conversazione..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><Search /></InputAdornment>
            }}
          />
        </Box>

        {/* Lista conversazioni */}
        <List sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 0 }}>
          {loadingConv ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : filteredConv.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
              Nessuna conversazione
            </Typography>
          ) : (
            filteredConv.map((conv, idx) => {
              const picSrc = getProfilePicSrc(conv);
              return (
                <React.Fragment key={`${conv.whatsapp_line_id || 'default'}:${conv.phone_number}`}>
                  <ListItemButton
                    selected={selectedConv?.phone_number === conv.phone_number && Number(selectedConv?.whatsapp_line_id || 0) === Number(conv.whatsapp_line_id || 0)}
                    onClick={() => setSelectedConv(conv)}
                    sx={{ py: 1.5, '&:hover .hide-btn': { opacity: 1 } }}
                  >
                    <ListItemAvatar>
                      <Badge badgeContent={conv.unread_count} color="success" max={99}>
                        <Avatar
                          src={picSrc || undefined}
                          sx={{ 
                            bgcolor: conv.customer ? 'primary.main' : (conv.suggested_customer ? 'grey.400' : 'grey.400'),
                            border: conv.suggested_customer && !conv.customer ? '2px solid #ed6c02' : 'none'
                          }}
                          imgProps={{ onError: () => setProfilePicErrors(prev => ({ ...prev, [conv.phone_number]: true })) }}
                        >
                          {(conv.customer?.nome || conv.contact_name || '').charAt(0)?.toUpperCase() || <Person />}
                        </Avatar>
                      </Badge>
                    </ListItemAvatar>
                    <ListItemText
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Typography variant="subtitle2" noWrap>
                          {conv.customer?.nome || conv.contact_name || conv.phone_number}
                        </Typography>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                            {conv.last_message?.direction === 'outbound' && '✓ '}
                            {conv.last_message?.message_type !== 'text' && !conv.last_message?.body ? '📎 Media' : conv.last_message?.body || ''}
                          </Typography>
                          {viewingAllLines && conv.whatsapp_line_id && (
                            <Chip
                              label={getLineLabel(conv.whatsapp_line_id, conv.business_phone_number)}
                              size="small"
                              variant="outlined"
                              sx={{ height: 20, mt: 0.3, mr: 0.5, fontSize: '0.7rem', maxWidth: 180, '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                          {conv.suggested_customer && !conv.customer && (
                            <Chip
                              icon={<LinkRounded sx={{ fontSize: 14 }} />}
                              label={conv.suggested_customer.nome}
                              size="small"
                              color="warning"
                              variant="outlined"
                              onClick={(e) => handleQuickLink(e, conv)}
                              sx={{ height: 20, mt: 0.3, fontSize: '0.7rem', maxWidth: 180, '& .MuiChip-label': { px: 0.5 } }}
                            />
                          )}
                        </Box>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                      {formatTime(conv.last_message_at)}
                    </Typography>
                    <Tooltip title="Nascondi contatto">
                      <IconButton
                        className="hide-btn"
                        size="small"
                        sx={{ opacity: 0, transition: 'opacity 0.2s', ml: 0.5, p: 0.3 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setHideDialogPhone(conv.phone_number);
                          setHideDialogName(conv.customer?.nome || conv.contact_name || conv.phone_number);
                        }}
                      >
                        <VisibilityOff sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItemButton>
                  {idx < filteredConv.length - 1 && <Divider />}
                </React.Fragment>
              );
            })
          )}
        </List>
      </Paper>
      )}

      {/* Area Chat */}
      {(!isMobile || selectedConv) && (
      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        {!selectedConv ? (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Stack alignItems="center" spacing={2}>
              <WhatsApp sx={{ fontSize: 80, color: 'text.disabled' }} />
              <Typography color="text.secondary">Seleziona una conversazione</Typography>
            </Stack>
          </Box>
        ) : (
          <>
            {/* Header chat */}
            <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2 }, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1.5 }}>
              {isMobile && (
                <IconButton size="small" onClick={() => {
                  setAiPanelOpen(false);
                  setSelectedConv(null);
                }}>
                  <ArrowBack />
                </IconButton>
              )}
              <Avatar
                src={getProfilePicSrc(selectedConv) || undefined}
                sx={{ bgcolor: selectedConv.customer ? 'primary.main' : 'grey.400' }}
                imgProps={{ onError: () => setProfilePicErrors(prev => ({ ...prev, [selectedConv.phone_number]: true })) }}
              >
                {(selectedConv.customer?.nome || selectedConv.contact_name || '').charAt(0)?.toUpperCase() || <Person />}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {selectedConv.customer?.nome || selectedConv.contact_name || selectedConv.phone_number}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap">
                  <Typography variant="caption" color="text.secondary">
                    <Phone sx={{ fontSize: 12, mr: 0.5 }} />
                    {selectedConv.phone_number}
                  </Typography>
                  {selectedConv.customer ? (
                    <>
                      <Chip 
                        label={`Cod: ${selectedConv.customer.cod_cli}`} 
                        size="small" 
                        variant="outlined" 
                        sx={{ height: 20 }}
                        onClick={() => window.open(`/crm/clienti/${selectedConv.customer.id}`, '_blank', 'noopener,noreferrer')}
                        onDelete={() => window.open(`/crm/clienti/${selectedConv.customer.id}`, '_blank', 'noopener,noreferrer')}
                        deleteIcon={<OpenInNew sx={{ fontSize: 14 }} />}
                        clickable
                      />
                      <Tooltip title="Rimuovi associazione cliente">
                        <IconButton size="small" onClick={handleRemoveCustomer} sx={{ p: 0.3 }}>
                          <LinkOff sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Analisi AI conversazione">
                        <IconButton size="small" color="primary" onClick={handleAiAnalyze} sx={{ p: 0.3 }}>
                          <AiIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  ) : (
                    <>
                      {selectedConv.suggested_customer && (
                        <Chip
                          icon={<LinkRounded sx={{ fontSize: 14 }} />}
                          label={`Collega: ${selectedConv.suggested_customer.nome}`}
                          size="small"
                          color="warning"
                          clickable
                          onClick={(e) => handleQuickLink(e, selectedConv)}
                          sx={{ height: 22 }}
                        />
                      )}
                      <Tooltip title="Associa cliente">
                        <IconButton size="small" color="primary" onClick={() => setAssignDialogOpen(true)} sx={{ p: 0.3 }}>
                          <PersonAdd sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {selectedConv.whatsapp_line_id && (
                    <Chip
                      label={getLineLabel(selectedConv.whatsapp_line_id, selectedConv.business_phone_number)}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20 }}
                    />
                  )}
                </Stack>
              </Box>
            </Paper>

            {/* Messaggi */}
            <Box 
              sx={{ 
                flex: 1, minHeight: 0, overflow: 'auto', p: { xs: 1, sm: 2 }, 
                bgcolor: '#e5ddd5',
                backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'100\' height=\'100\' viewBox=\'0 0 100 100\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cpath d=\'M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3z\' fill=\'%23d4cdc4\' fill-opacity=\'0.15\' fill-rule=\'evenodd\'/%3E%3C/svg%3E")'
              }}
            >
              {loadingThread ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : messages.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
                  Nessun messaggio
                </Typography>
              ) : (
                messages.map((msg) => (
                  <MessageBubble key={msg.id} msg={msg} onDownloadMedia={handleDownloadMedia} />
                ))
              )}
              <div ref={messagesEndRef} />
            </Box>

            {/* Preview allegato */}
            {attachFile && (
              <Paper elevation={0} sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', gap: 1, bgcolor: 'grey.50' }}>
                <InsertDriveFile sx={{ color: 'primary.main' }} />
                <Typography variant="body2" sx={{ flex: 1 }} noWrap>{attachFile.name}</Typography>
                <Typography variant="caption" color="text.secondary">{(attachFile.size / 1024).toFixed(0)} KB</Typography>
                <IconButton size="small" onClick={() => setAttachFile(null)}><Close fontSize="small" /></IconButton>
              </Paper>
            )}

            {/* Input messaggio */}
            <Paper elevation={0} sx={{ p: 1.5, borderTop: 1, borderColor: 'divider', display: 'flex', gap: 1, alignItems: 'flex-end' }}>
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={(e) => {
                  if (e.target.files?.[0]) setAttachFile(e.target.files[0]);
                  e.target.value = '';
                }}
              />
              <Tooltip title="Allega file">
                <IconButton
                  onClick={() => fileInputRef.current?.click()}
                  color={attachFile ? 'primary' : 'default'}
                >
                  <AttachFile />
                </IconButton>
              </Tooltip>
              <TextField
                fullWidth
                multiline
                maxRows={4}
                size="small"
                placeholder={attachFile ? 'Didascalia (opzionale)...' : 'Scrivi un messaggio...'}
                value={messageText}
                onChange={(e) => setMessageText(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={sending || !selectedConversationReady}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 3 } }}
              />
              <IconButton 
                color="primary" 
                onClick={handleSend} 
                disabled={(!messageText.trim() && !attachFile) || sending || !selectedConversationReady}
                sx={{ 
                  bgcolor: 'success.main', color: 'white', 
                  '&:hover': { bgcolor: 'success.dark' },
                  '&.Mui-disabled': { bgcolor: 'grey.300' }
                }}
              >
                {sending ? <CircularProgress size={24} color="inherit" /> : <Send />}
              </IconButton>
            </Paper>
          </>
        )}
      </Box>
      )}
      </Box>

      {renderSetupDialog()}

      {/* Dialog Associa Cliente */}
      <AssignCustomerDialog
        open={assignDialogOpen}
        onClose={() => setAssignDialogOpen(false)}
        phone={selectedConv?.phone_number}
        lineId={selectedConv?.whatsapp_line_id || activeLineId || null}
        onAssigned={handleCustomerAssigned}
        fullScreen={isMobile}
      />

      {/* Dialog Conferma Nascondi Contatto */}
      <Dialog open={!!hideDialogPhone} onClose={() => setHideDialogPhone(null)} fullScreen={isMobile}>
        <DialogTitle>Nascondi contatto</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Vuoi nascondere <strong>{hideDialogName}</strong> dalla lista chat?
            <br /><br />
            Il contatto non apparirà più tra le conversazioni. Potrai ripristinarlo dalle impostazioni.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHideDialogPhone(null)}>Annulla</Button>
          <Button
            variant="contained"
            color="warning"
            startIcon={<VisibilityOff />}
            onClick={() => handleHideContact(hideDialogPhone, hideDialogName)}
          >
            Nascondi
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI WhatsApp Panel */}
      <AiWhatsappPanel
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        phoneNumber={selectedConv?.phone_number}
        customerId={selectedConv?.customer_id || selectedConv?.customer?.id}
        analysis={aiAnalysis}
        actionId={aiActionId}
        invoicesData={aiInvoicesData}
        loading={aiLoading}
        error={aiError}
        onAnalyze={handleAiAnalyze}
        onApprove={handleAiApprove}
        onReject={handleAiReject}
        onRegenerate={handleAiRegenerate}
        onEditDraft={handleAiEditDraft}
        onInsertDraft={handleAiInsertDraft}
      />
    </Box>
  );
}
